import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LngLat, Map, Marker } from 'maplibre-gl';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, first, skip } from 'rxjs/operators';
import device from 'current-device';
import { MapService } from './map.service';
import { MapDrawerHelperService } from './map-drawer-helper.service';
import { WaypointService } from '../core/waypoint.service';
import { UtilsService } from '../utils/utils.service';
import { SettingsService } from '../account/settings.service';
import { AccountService } from '../account/account.service';
import { LanguageService } from '../account/language.service';
import { MobileResolutionService } from '../platform/mobile-resolution.service';
import { WidgetService } from '../platform/widget.service';
import { ChargingStationDetails, ChargerProvider } from 'src/app/models/charging-station';
import { Route, RoutePlanElement, RoutePlanType } from 'src/app/models/route';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DisplayMode } from 'src/app/models/display-mode';
import { SvgRecolorService } from '../media/svg-recolor.service';
import { VehicleService } from '../vehicle/vehicle.service';

export const enum PopupAlign {
  Left,
  Right,
  Top
}

@Injectable({
  providedIn: 'root',
})
export class MapChargingPopupService {

  private mapInstance: Map | null = null;
  private lastOpenedChargingStationPopup: ChargingStationDetails | null = null;
  // Layout and flags of the last draw, so the popup can be rebuilt in place.
  private lastPopupLayout: {
    align: PopupAlign, widthAnchor: number, heightAnchor: number,
    statusSelector: boolean, planBtn: boolean, searched: boolean
  } | null = null;
  private chargingStationPopupMarker: Marker | null = null;
  private chargerPopupSubscriptions: Subscription[] = [];
  private lastRouteData: Route | null = null;

  public statusChanged$ = new Subject<void>();

  private readonly mapService = inject(MapService);
  private readonly mapDrawerHelperService = inject(MapDrawerHelperService);
  private readonly waypointService = inject(WaypointService);
  private readonly utilsService = inject(UtilsService);
  private readonly settingsService = inject(SettingsService);
  private readonly accountService = inject(AccountService);
  private readonly languageService = inject(LanguageService);
  private readonly mobileResolutionService = inject(MobileResolutionService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly svgRecolorService = inject(SvgRecolorService);
  private readonly widgetService = inject(WidgetService);
  private readonly vehicleService = inject(VehicleService);

  constructor() {
    // The popup markup bakes the skin in: applySvgRecolors() reads a skin-bound
    // CSS variable at draw time and the icon paths carry the skin folder. A popup
    // left open across a skin switch would keep the old colors, so rebuild it.
    // ObservableMap also emits on the POI toggle; only a real skin change counts.
    // setMap() flips the body class before emitting, so the redraw already sees
    // the new skin's variables.
    this.settingsService.ObservableMap.pipe(
      distinctUntilChanged((prev, next) => prev?.Skin === next?.Skin),
      skip(1),
      takeUntilDestroyed()
    ).subscribe(() => this.redrawOpenedPopupForSkin());
  }

  public init(map: Map): void {
    this.mapInstance = map;
  }

  public destroy(): void {
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];
    this.removeChargingStationPopup();
    this.mapInstance = null;
  }

  public getLastOpenedPopup(): ChargingStationDetails | null {
    return this.lastOpenedChargingStationPopup;
  }

  public setLastRouteData(routeData: Route | null): void {
    this.lastRouteData = routeData;
  }

  // ─── Charging Station Popup ──────────────────────────────────────────

  /**
   * Fetches the station details and opens the popup. `fallbackCoords` is the
   * position the caller already knows (clicked feature, route element, ...); it
   * anchors the popup when the backend answers without coordinates, e.g. with a
   * bare `{ id }` for a station it has no record of.
   */
  public loadChargingStationPopup(align: PopupAlign, widthAnchor: number, heightAnchor: number,
    chargingStationId: string, statusSelector: boolean, planBtn: boolean, searched: boolean,
    fallbackCoords?: { lat: number, lon: number }): void {
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];

    this.mapService.getChargingStationDetail([chargingStationId]).subscribe((resp) => {
      const chargingStation = this.withDefaults(resp?.[0], chargingStationId, fallbackCoords);
      if (chargingStation) {
        this.drawChargingStationPopup(align, widthAnchor, heightAnchor, chargingStation, statusSelector, planBtn, searched);
      }
    });
  }

  // Fills in whatever the detail response left out so the popup can always be
  // drawn with default content. Returns null only when no position is known at
  // all, because a marker cannot be placed without one.
  private withDefaults(details: ChargingStationDetails | undefined, chargingStationId: string,
    fallbackCoords?: { lat: number, lon: number }): ChargingStationDetails | null {
    const lat = Number.isFinite(details?.lat) ? details!.lat : fallbackCoords?.lat;
    const lon = Number.isFinite(details?.lon) ? details!.lon : fallbackCoords?.lon;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return {
      id: details?.id ?? chargingStationId,
      provider: details?.provider ?? this.mapService.getChargerProviderById(chargingStationId),
      lat: lat!,
      lon: lon!,
      plugs: details?.plugs ?? [],
      img: details?.img ?? '',
      opid: details?.opid ?? -1,
      cost: details?.cost ?? '',
      email: details?.email ?? '',
      phone: details?.phone ?? '',
      title: details?.title || this.languageService.languageJSON.Global_ChargingStation || 'Charging Station',
    };
  }

  private drawChargingStationPopup(align: PopupAlign, widthAnchor: number, heightAnchor: number,
    chargingStation: ChargingStationDetails, statusSelector: boolean, planBtn: boolean, searched: boolean,
    recenter: boolean = true): void {
    // Widget `stationlists=0`: no favorite / disabled station lists, so no selector to fill them.
    statusSelector = statusSelector && this.widgetService.stationListsEnabled();
    this.lastOpenedChargingStationPopup = chargingStation;
    this.lastPopupLayout = { align, widthAnchor, heightAnchor, statusSelector, planBtn, searched };
    this.removeChargingStationPopup();

    const map = this.mapInstance!;

    let tooltip = "";

    switch (align) {
      case PopupAlign.Left:
        tooltip += "<div class='map-tooltip arrow-to-right cs-tooltip ev-card'>";
        break;
      case PopupAlign.Right:
        tooltip += "<div class='map-tooltip arrow-to-left cs-tooltip ev-card'>";
        break;
      case PopupAlign.Top:
        if (planBtn || searched) {
          tooltip += "<div class='map-tooltip arrow-to-bottom top cs-tooltip ev-card'>";
        } else {
          tooltip += "<div class='map-tooltip arrow-to-bottom top rounded-body cs-tooltip ev-card'>";
        }
        break;
    }

    // Title - clean address text
    tooltip += `<div class="cs-title">${this.escapeHtml(chargingStation.title)}</div>`;

    tooltip += `<div class="body">`;

    // Operator & info card
    let operatorName = '';
    let operatorUrl = '';
    if (chargingStation.opid != undefined && chargingStation.opid != null && chargingStation.opid != -1) {
      const operator = this.mapService.getChargingOperatorByID(chargingStation.opid);
      if (operator) {
        operatorName = operator.name;
        operatorUrl = operator.url || '';
      }
    }

    const hasInfo = operatorName || chargingStation.phone || chargingStation.cost || chargingStation.provider == ChargerProvider.OCM;

    if (hasInfo) {
      tooltip += `<div class="cs-info-card">`;

      if (operatorName) {
        tooltip += `<div class="cs-operator-name">${this.escapeHtml(operatorName)}</div>`;
      }

      if (operatorUrl) {
        tooltip += `<div class="cs-info-row">
          <span class="cs-info-icon" data-recolor-src="assets/icons/dark/map/info_icon_web.svg"></span>
          <a target="_blank" href="${this.escapeHtml(operatorUrl)}">${this.escapeHtml(operatorUrl)}</a></div>`;
      }

      if (chargingStation.phone) {
        tooltip += `<div class="cs-info-row">
          <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_phone.svg" />
          <a target="_blank" href="tel:${this.escapeHtml(chargingStation.phone)}">${this.escapeHtml(chargingStation.phone)}</a></div>`;
      }

      if (chargingStation.cost) {
        if (this.utilsService.validURL(chargingStation.cost)) {
          tooltip += `<div class="cs-info-row">
            <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_price.svg" />
            <a target="_blank" href="${this.escapeHtml(chargingStation.cost)}">${this.escapeHtml(chargingStation.cost)}</a></div>`;
        } else {
          tooltip += `<div class="cs-info-row">
            <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_price.svg" />
            <span>${this.escapeHtml(chargingStation.cost)}</span></div>`;
        }
      }

      if (chargingStation.provider == ChargerProvider.OCM) {
        const ocmId = this.utilsService.getOCMId(chargingStation.id);
        tooltip += `<div class="cs-info-row">
          <img class="cs-info-icon" src="assets/icons/default/map/info_icon_ocm.svg" />
          <a target="_blank" href="https://openchargemap.org/site/poi/details/${this.escapeHtml(ocmId)}">${this.languageService.languageJSON.Map_Popup_MoreInfo}</a></div>`;
      }

      tooltip += `</div>`;
    }

    // Plug card
    const plugs = chargingStation.plugs ?? [];
    if (plugs.length > 0) {
      tooltip += `<div class="cs-plug-card">`;
      for (let j = 0; j < plugs.length; j++) {
        const chargePower = Math.round(chargingStation.plugs[j].power * 10) / 10;
        const chargerType = chargingStation.plugs[j].type;
        const carPower = chargerType == 3 || chargerType == 5 || chargerType == 6
          ? this.vehicleService.getSelectedCar().ChargePower
          : this.vehicleService.getSelectedCar().FastChargePower;

        const chargePowerText = (carPower > 0 && carPower < chargePower)
          ? chargePower + "kW (" + carPower + " kW)"
          : chargePower + " kW";

        const isFast = chargePower >= 50;
        const powerClass = isFast ? 'fast' : 'standard';

        tooltip += `<div class="cs-plug-row ${powerClass}">
          <div class="charger-icon"><span data-recolor-src="/assets/icons/dark/plug/${this.utilsService.chargingstationsImg[chargingStation.plugs[j].type - 1][0]}"></span>`;

        if (this.accountService.getUserSubscriptionType().includes('proplus.') && (chargingStation.plugs[j].statusCount ?? 0) > 0) {
          tooltip += `<div class="cs-availability ${chargingStation.plugs[j].available! > 0 ? 'available' : 'not-available'}">${chargingStation.plugs[j].available}/${chargingStation.plugs[j].statusCount}</div>`;
        }

        tooltip += `</div>
          <span class="cs-plug-text">${chargingStation.plugs[j].count} x ${this.utilsService.chargingstationsImg[chargingStation.plugs[j].type - 1][1]} - ${chargePowerText}</span></div>`;
      }
      tooltip += `</div>`;
    }

    // Status selector
    if (statusSelector) {
      tooltip += `<div class="cs-status-wrapper">`;
      tooltip += `<div class="cs-status-card status status-opener">`;
      if (this.settingsService.getContainsDisabledChargingStationId(chargingStation.id)) {
        tooltip += this.getChargerDisabledStatusHtmlElementsSelected();
      } else if (this.settingsService.getContainsFavoriteChargingStationId(chargingStation.id)) {
        tooltip += this.getChargerFavoriteStatusHtmlElementsSelected();
      } else {
        tooltip += this.getChargerNormalStatusHtmlElementsSelected();
      }
      tooltip += `<img class="status-opener-icon" src="assets/icons/${this.settingsService.getMap().Skin}/ui/open_arrow.svg" /></div>`;

      tooltip += `<div class="status-dropdown">
        <div class="status" id="normal-btn">${this.getChargerNormalStatusHtmlElements()}</div>
        <div class="divider"></div>
        <div class="status" id="favorite-btn">${this.getChargerFavoriteStatusHtmlElements()}</div>
        <div class="divider"></div>
        <div class="status" id="disabled-btn">${this.getChargerDisabledStatusHtmlElements()}</div>
      </div>`;
      tooltip += `</div>`;
    }
    tooltip += `</div>`;

    if (planBtn) {
      if (this.waypointService.getWaypointsParams().length >= 5) {
        tooltip += "<div class='ev-button primary plan-btn inactive'>" + this.languageService.languageJSON.Map_Popup_ChargeHere + "</div>";
      } else {
        tooltip += "<div class='ev-button primary plan-btn'>" + this.languageService.languageJSON.Map_Popup_ChargeHere + "</div>";
      }
    }
    if (searched) {
      tooltip += "<div class='plan-btn plan-btn-container'></div>";
    }
    tooltip += "</div>";

    // Create a 0x0 anchor element so MapLibre's translate(-50%, -50%) has no effect
    const popupEl = document.createElement('div');

    // Position the tooltip via an absolutely-positioned wrapper
    const tooltipWrapper = document.createElement('div');
    tooltipWrapper.innerHTML = tooltip;
    tooltipWrapper.style.position = 'absolute';

    switch (align) {
      case PopupAlign.Left:
        tooltipWrapper.style.right = `${Math.abs(widthAnchor)}px`;
        tooltipWrapper.style.transform = 'translateY(-50%)';
        break;
      case PopupAlign.Right:
        tooltipWrapper.style.left = `${Math.abs(widthAnchor)}px`;
        tooltipWrapper.style.transform = 'translateY(-50%)';
        break;
      case PopupAlign.Top:
        tooltipWrapper.style.bottom = `${heightAnchor}px`;
        tooltipWrapper.style.transform = 'translateX(-50%)';
        break;
    }

    popupEl.appendChild(tooltipWrapper);

    this.chargingStationPopupMarker = new Marker({ element: popupEl })
      .setLngLat([chargingStation.lon, chargingStation.lat])
      .addTo(map);

    this.applySvgRecolors(tooltipWrapper);
    this.mapDrawerHelperService.cardOpened(popupEl);
    if (recenter) {
      // On the wide layout the planning side panel covers the left of the map
      // in route mode, so nudge the popup towards the right half to keep it
      // clear. The compact layout hides the panel while a card is open instead.
      const shiftX = !this.mobileResolutionService.getMobileResolution()
        && this.waypointService.getSelectedDisplayMode() == DisplayMode.ROUTE ? 125 : 0;
      const tooltipEl = tooltipWrapper.querySelector<HTMLElement>('.map-tooltip') ?? tooltipWrapper;
      this.mapDrawerHelperService.revealCard([chargingStation.lon, chargingStation.lat], tooltipEl, { shiftX });
    }

    // Bind event listeners after DOM is ready
    setTimeout(() => {
      this.bindPopupEventListeners(chargingStation, statusSelector, planBtn);
    });
  }

  private applySvgRecolors(root: HTMLElement): void {
    const color = getComputedStyle(document.body)
      .getPropertyValue('--gradient-color-over-gradient').trim();

    root.querySelectorAll<HTMLElement>('[data-recolor-src]').forEach(placeholder => {
      const src = placeholder.getAttribute('data-recolor-src')!;
      const cls = placeholder.getAttribute('class') ?? '';
      this.chargerPopupSubscriptions.push(
        this.svgRecolorService.recolor(src, { color }).subscribe(svgMarkup => {
          placeholder.outerHTML = svgMarkup.replace('<svg', `<svg class="${cls}"`);
        })
      );
    });
  }

  private bindPopupEventListeners(chargingStation: ChargingStationDetails, statusSelector: boolean, planBtn: boolean): void {
    if (planBtn) {
      if (this.waypointService.getWaypointsParams().length >= 5) {
        this.snackBar.open(this.languageService.languageJSON.InfoSidebar_WaypointLimitReached, "",
          { duration: 5000, panelClass: 'snackbar-info' });
      }

      const planBtnEl = document.getElementsByClassName('plan-btn')[0];
      if (planBtnEl) {
        this.chargerPopupSubscriptions.push(
          fromEvent(planBtnEl, 'click').pipe(first()).subscribe((event: any) => {
            if (event.isTrusted) {
              this.removeChargingStationPopup();
              this.waypointService.addChargingStationWaypoint(
                chargingStation.title,
                chargingStation.lat,
                chargingStation.lon,
                chargingStation.id,
                this.getChargeHereClosestRouteIndex(chargingStation)
              );
            }
          })
        );
      }
    }

    if (statusSelector) {
      const openerEl = document.getElementsByClassName('status-opener')[0];
      const dropdownEl = document.getElementsByClassName('status-dropdown')[0];
      const tooltipEl = document.getElementsByClassName('cs-tooltip')[0];
      const normalBtn = document.getElementById('normal-btn');
      const favoriteBtn = document.getElementById('favorite-btn');
      const disabledBtn = document.getElementById('disabled-btn');

      if (openerEl) {
        this.chargerPopupSubscriptions.push(fromEvent(openerEl, 'click').subscribe((e: any) => {
          if (e.isTrusted && dropdownEl) { dropdownEl.classList.add("show"); }
        }));
      }
      if (dropdownEl) {
        this.chargerPopupSubscriptions.push(fromEvent(dropdownEl, 'click').subscribe((e: any) => {
          if (e.isTrusted) { dropdownEl.classList.remove("show"); }
        }));
      }
      if (tooltipEl) {
        this.chargerPopupSubscriptions.push(fromEvent(tooltipEl, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            let removeClass = true;
            const path = e.composedPath();
            if (path) {
              for (let i = 0; i < path.length; i++) {
                if (path[i].className && path[i].className?.includes("status status-opener") ||
                  path[i].className && path[i].className?.includes("status-dropdown")) {
                  removeClass = false;
                  break;
                }
              }
            }
            if (removeClass && dropdownEl) { dropdownEl.classList.remove("show"); }
          }
        }));
      }
      if (normalBtn) {
        this.chargerPopupSubscriptions.push(fromEvent(normalBtn, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            this.settingsService.setToNormalChargingStation(chargingStation.id);
            this.changeStatusOpener(this.getChargerNormalStatusHtmlElementsSelected());
          }
        }));
      }
      if (favoriteBtn) {
        this.chargerPopupSubscriptions.push(fromEvent(favoriteBtn, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            this.settingsService.setToFavoriteChargingStation(chargingStation.id);
            this.changeStatusOpener(this.getChargerFavoriteStatusHtmlElementsSelected());
          }
        }));
      }
      if (disabledBtn) {
        this.chargerPopupSubscriptions.push(fromEvent(disabledBtn, 'click').subscribe((e: any) => {
          if (e.isTrusted) {
            this.settingsService.setToDisabledChargingStation(chargingStation.id);
            this.changeStatusOpener(this.getChargerDisabledStatusHtmlElementsSelected());
          }
        }));
      }
    }
  }

  public removeChargingStationPopup(): void {
    if (this.chargingStationPopupMarker) {
      this.mapDrawerHelperService.cardClosed(this.chargingStationPopupMarker.getElement());
      this.chargingStationPopupMarker.remove();
      this.chargingStationPopupMarker = null;
    }
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];
  }

  // Re-draws the currently open charging-station popup (if any) so its imperatively
  // built HTML picks up newly loaded translation texts. No-op when no popup is visible.
  public redrawOpenedChargingStationPopup(): void {
    if (this.chargingStationPopupMarker && this.lastOpenedChargingStationPopup) {
      this.redrawChargingStationPopup(this.lastOpenedChargingStationPopup);
    }
  }

  // Rebuilds the open popup after a skin change with the exact layout it was
  // drawn with (a route charger popup opens Left/Right-aligned, which the
  // generic redraw above would turn into a Top one) and without panning the
  // map, since the user is mid-settings and the popup hasn't moved.
  private redrawOpenedPopupForSkin(): void {
    if (!this.mapInstance || !this.chargingStationPopupMarker ||
      !this.lastOpenedChargingStationPopup || !this.lastPopupLayout) {
      return;
    }
    const layout = this.lastPopupLayout;
    this.drawChargingStationPopup(layout.align, layout.widthAnchor, layout.heightAnchor,
      this.lastOpenedChargingStationPopup, layout.statusSelector, layout.planBtn, layout.searched, false);
  }

  public redrawChargingStationPopup(chargingStationPopup: ChargingStationDetails): void {
    let chargerPlannedRouteOccurence: number = 0;
    if (this.lastRouteData) {
      const routeChargers: RoutePlanElement[] = this.lastRouteData.RoutePlan.filter((routePlanEl) => {
        return routePlanEl.Type == RoutePlanType.Charger;
      });
      chargerPlannedRouteOccurence = routeChargers.filter((routeCharger) =>
        routeCharger.OperatorId == chargingStationPopup.id
      ).length;
    }
    this.removeChargingStationPopup();
    const favoriteCharger = this.settingsService.getContainsFavoriteChargingStationId(chargingStationPopup.id);

    if (chargerPlannedRouteOccurence == 0) {
      const heightAnchor = favoriteCharger ? 24 : 20;
        this.drawChargingStationPopup(PopupAlign.Top, 0, heightAnchor, chargingStationPopup, true, true, false);
    } else if (chargerPlannedRouteOccurence == 1) {
      this.drawChargingStationPopup(PopupAlign.Top, 0, 80, chargingStationPopup, true, false, false);
    } else {
      this.drawChargingStationPopup(PopupAlign.Top, 0, 61 + (19 * chargerPlannedRouteOccurence), chargingStationPopup, true, false, false);
    }
  }

  // ─── Status Helpers ──────────────────────────────────────────────────

  private changeStatusOpener(htmlEl: string): void {
    const statusOpener = document.querySelector(".status.status-opener");
    if (statusOpener) {
      const icon = statusOpener.querySelector(".status-icon");
      const state = statusOpener.querySelector(".status-state");
      if (icon) icon.remove();
      if (state) state.remove();
      const statusEL = document.createElement('div');
      statusEL.innerHTML = htmlEl;
      for (let i = statusEL.children.length - 1; i >= 0; i--) {
        statusOpener.prepend(statusEL.children[i]);
      }
    }
    if (this.accountService.getIsAuthorized()) {
      this.accountService.setDisabledChargingStations().subscribe();
      this.accountService.setFavoriteChargingStations().subscribe();
    }
    this.statusChanged$.next();
  }

  private getChargerNormalStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "normal",
      this.languageService.languageJSON.Global_Normal, '');
  }

  private getChargerNormalStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "normal",
      this.languageService.languageJSON.Global_Normal, this.languageService.languageJSON.Map_Popup_Status);
  }

  private getChargerDisabledStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "disabled",
      this.languageService.languageJSON.Map_Popup_Disabled, '');
  }

  private getChargerDisabledStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "disabled",
      this.languageService.languageJSON.Map_Popup_Disabled, this.languageService.languageJSON.Map_Popup_Status);
  }

  private getChargerFavoriteStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "favorite",
      this.languageService.languageJSON.Map_Popup_Favorite, '');
  }

  private getChargerFavoriteStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "favorite",
      this.languageService.languageJSON.Map_Popup_Favorite, this.languageService.languageJSON.Map_Popup_Status);
  }

  // ─── Utility Methods ─────────────────────────────────────────────────

  private getChargeHereClosestRouteIndex(chargingStation: ChargingStationDetails): number {
    let closestRoutePointIdx = -1;
    let closestRoutePointDistance = -1;

    if (this.waypointService.getSelectedDisplayMode() == DisplayMode.RANGE) {
      return this.waypointService.getWaypointsParams().length;
    }

    if (this.lastRouteData) {
      for (let i = 0; i < this.lastRouteData.RoutePoints.length; i++) {
        const actDist = this.utilsService.distanceBetweenCoordinates(
          [chargingStation.lat, chargingStation.lon],
          [this.lastRouteData.RoutePoints[i].Location.lat, this.lastRouteData.RoutePoints[i].Location.lng]);
        if (actDist < 30 * 1000 && (closestRoutePointDistance == -1 || actDist < closestRoutePointDistance)) {
          closestRoutePointIdx = i;
          closestRoutePointDistance = actDist;
        }
      }

      if (closestRoutePointIdx != -1) {
        let i = 0;
        let reachedWaypoints = 0;
        while (this.lastRouteData.Turns[i] && this.lastRouteData.Turns[i].RoutePointIdxStart <= closestRoutePointIdx) {
          if (this.lastRouteData.Turns[i].TypeIndex == 103 || this.lastRouteData.Turns[i].TypeIndex == 98 ||
            this.lastRouteData.Turns[i].TypeIndex == 106) {
            reachedWaypoints++;
          }
          i++;
        }
        return reachedWaypoints + 1;
      } else {
        return this.waypointService.getWaypointsParams().length;
      }
    } else {
      return this.waypointService.getWaypointsParams().length;
    }
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
}
